/**
 * <lk-klaviyo-form> — submits the design-styled coming-soon email form
 * straight to Klaviyo's client subscriptions API (the same list the old
 * embedded form XyYnEL fed), keeping our markup/CSS instead of Klaviyo's.
 *
 * Attributes:
 *   data-company-id  Klaviyo public site id (company_id)
 *   data-list-id     Klaviyo list id to subscribe to
 *   data-source      custom_source label recorded on the subscription
 *
 * Contract: wraps a <form> containing an <input type="email"> and a
 * [data-lk-note] element for success/error text (data-success/data-error
 * strings on the wrapper). Falls back to normal form submission when fetch
 * is unavailable.
 */
class LkKlaviyoForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.note = this.querySelector('[data-lk-note]');
    if (!this.form || !window.fetch) return;
    this.handleSubmit = (e) => this.submit(e);
    this.form.addEventListener('submit', this.handleSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.handleSubmit);
  }

  async submit(e) {
    e.preventDefault();
    const email = this.form.querySelector('input[type="email"]')?.value.trim();
    if (!email) return;
    const button = this.form.querySelector('button[type="submit"]');
    if (button) button.disabled = true;
    try {
      const res = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.dataset.companyId)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: '2024-10-15',
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: this.dataset.source || 'Coming Soon Landing',
                profile: {
                  data: { type: 'profile', attributes: { email } },
                },
              },
              relationships: {
                list: { data: { type: 'list', id: this.dataset.listId } },
              },
            },
          }),
        }
      );
      if (res.status === 202 || res.ok) {
        this.showNote(this.dataset.success || 'Thanks for signing up!', 'status');
        this.form.reset();
      } else {
        this.showNote(this.dataset.error || 'Something went wrong — please try again.', 'alert');
      }
    } catch {
      this.showNote(this.dataset.error || 'Something went wrong — please try again.', 'alert');
    } finally {
      if (button) button.disabled = false;
    }
  }

  showNote(text, role) {
    if (!this.note) return;
    this.note.textContent = text;
    this.note.setAttribute('role', role);
    this.note.hidden = false;
  }
}

if (!customElements.get('lk-klaviyo-form')) {
  customElements.define('lk-klaviyo-form', LkKlaviyoForm);
}
